/* eslint-disable import/prefer-default-export */
import {
	ATTRIBUTES as BASE_ATTRIBUTES,
	ENDSCREEN_OPERANDS as BASE_ENDSCREEN_OPERANDS,
	EVENTS as BASE_EVENTS,
	HEADER_LABELS as BASE_HEADER_LABELS,
	ROUND_ATTRIBUTES as BASE_ROUND_ATTRIBUTES,
	SCORE_TYPES as BASE_SCORE_TYPES,
} from '../baseGame.types';

/**
 * @typedef {import('../baseGame.types').BaseRoundOptions} BaseRoundOptions
 * @typedef {import('../baseGame.types').BaseGameOptions} BaseGameOptions
 *
 *
 * @typedef {object} TriviaGamePartial
 * @property {boolean} [hideCorrectAnswer]
 * @property {number} [targetScore]
 * @property {number} [numLives]
 * @property {number} [livesSpent]
 * @property {Record<DURATIONS, number>} [durations]
 * @property {Record<HEADER_LABELS, string>} [labels]
 *
 *
 * @typedef {object} TriviaRoundPartial
 * @property {QUESTION_TYPES} questionType
 * * Scoring
 * @property {boolean} isScored
 * @property {number} [pointsPerQuestion]
 * * PreQuestion
 * @property {string} [video]
 * @property {string} [videoImage]
 * @property {string} [videoCaption]
 * * Question (common, standard, and swipe)
 * @property {string} [question]
 * @property {string} [questionImage]
 * @property {string} [questionImageCaption]
 * @property {string} [questionImageReveal]
 * @property {string} [questionVideo]
 * * * Swipe
 * @property {number} [rightIndex=0]
 * @property {number} [leftIndex=1]
 * @property {number} [bottomIndex=2]
 * @property {number} [topIndex=3]
 * * * Answers
 * @property {boolean} disableChoiceShuffle
 * @property {boolean} hasCorrectAnswer
 * @property {number} [correctChoiceIndex]
 * @property {string[]} choices
 * @property {string[][]} choicesValues
 * @property {string[]} answerImages
 * * PostQuestion
 * @property {string} [answerVideo]
 * @property {string} [answerImage]
 * @property {string} [answerVideoCaption]
 * * MadLib result round
 * @property {string} [resultTemplate]
 *
 *
 * @typedef {BaseGameOptions & TriviaGamePartial} TriviaGameOptions
 * @typedef {BaseRoundOptions & TriviaRoundPartial} TriviaRoundOptions
 */

/**
 * @enum {typeof ROUND_ATTRIBUTES[keyof typeof ROUND_ATTRIBUTES]}
 */
export const ROUND_ATTRIBUTES = Object.freeze({
	...BASE_ROUND_ATTRIBUTES,
	questionType: 'questionType',
	isScored: 'isScored',
	pointsPerQuestion: 'pointsPerQuestion',
	hasCorrectAnswer: 'hasCorrectAnswer',
	correctChoiceIndex: 'correctChoiceIndex',
	disableChoiceShuffle: 'disableChoiceShuffle',
	questionImage: 'questionImage',
	questionImageReveal: 'questionImageReveal',
	answerImages: 'answerImages',
	choices: 'choices',
	choicesValues: 'choicesValues',
	preQuestionVideo: 'video',
	preQuestionVideoImage: 'videoImage',
	preQuestionVideoCaption: 'videoCaption',
	questionVideo: 'questionVideo',
	postQuestionVideo: 'answerVideo',
	postQuestionVideoImage: 'answerImage',
	postQuestionVideoCaption: 'answerVideoCaption',
	resultTemplate: 'resultTemplate',
});

/**
 * @enum {typeof QUESTION_TYPES[keyof typeof QUESTION_TYPES]}
 */
export const QUESTION_TYPES = Object.freeze({
	madLibResult: 'madLibResult',
	standard: 'standard',
	swipeQuestion: 'swipeQuestion',
});

/**
 * @enum {typeof DURATIONS[keyof typeof DURATIONS]}
 */
export const DURATIONS = Object.freeze({
	correctAnswer: 'correctAnswer',
	incorrectAnswer: 'incorrectAnswer',
	revealImage: 'revealImage',
});

/**
 * @enum {typeof ENDSCREEN_OPERANDS[keyof typeof ENDSCREEN_OPERANDS]}
 */
export const ENDSCREEN_OPERANDS = Object.freeze({
	...BASE_ENDSCREEN_OPERANDS,
	lives: 'lives',
	group: 'group',
});

/**
 * @enum {typeof SCORE_TYPES[keyof typeof SCORE_TYPES]}
 */
export const SCORE_TYPES = Object.freeze({
	...BASE_SCORE_TYPES,
	grouping: 'grouping',
});

/**
 * @enum {typeof HEADER_LABELS[keyof typeof HEADER_LABELS]}
 */
export const HEADER_LABELS = Object.freeze({
	...BASE_HEADER_LABELS,
	lives: 'lives',
});

/**
 * @enum {typeof ATTRIBUTES[keyof typeof ATTRIBUTES]}
 */
export const ATTRIBUTES = Object.freeze({
	...BASE_ATTRIBUTES,
	durations: 'durations',
	hideCorrectAnswer: 'hideCorrectAnswer',
	targetScore: 'targetScore',
	numLives: 'numLives',
	livesSpent: 'livesSpent',
});

/** @enum {typeof EVENTS[keyof typeof EVENTS]} */
export const EVENTS = Object.freeze({
	...BASE_EVENTS,
	answerCorrect: 'answer-correct',
	answerIncorrect: 'answer-incorrect',
});
